<template>
  <div class="match-list-standard">
    <template v-for="(group, groupStatus) in groupedMatches" :key="groupStatus">
      <template v-if="group.length > 0">
        <div class="group-header">{{ getGroupTitle(groupStatus) }}</div>
        <div
          :class="['match-item', {
            'match-in-progress': match.match_status === 2,
            'match-not-started': match.match_status === 1
          }]"
          v-for="match in group"
          :key="match.match_id"
        >
          <!-- 表头行 -->
          <div class="match-header">
            <div class="header-left">
              <img :src="match.league_logo" alt="League Logo" class="league-logo" />
              <span class="league-name">{{ match.league_name }}</span>
            </div>
            <div class="header-columns">
              <span class="col-quarter">1</span>
              <span class="col-quarter">2</span>
              <span class="col-quarter">3</span>
              <span class="col-quarter">4</span>
              <span class="col-total">全场</span>
              <span class="col-half">上下</span>
              <span class="col-diff">分差</span>
              <span class="col-score">总分</span>
              <span class="col-live">直播</span>
              <span class="col-odds-title">胜负</span>
              <span class="col-odds-title">让分</span>
              <span class="col-odds-title">总分</span>
              <span class="col-actions">操作</span>
            </div>
          </div>

          <!-- 比赛数据区域 -->
          <div class="match-data-wrapper">
            <!-- 左侧固定区域：圆圈 + 状态 -->
            <div class="match-left-fixed">
              <div class="select-icon">
                <img src="@assets/icons/score/select.webp" alt="Select" />
              </div>
              <div class="status-info" :class="match.match_status === 2 && 'in-progress'">
                <div class="status-text">{{ match.match_status_txt }}</div>
                <div class="time-text" v-show="timeShowHandle(match.match_status)">
                  {{ formatTime(match.match_time) }}
                </div>
              </div>
            </div>
            
            <!-- 右侧主客队数据区域 -->
            <div class="match-right-data">
              <!-- 主队数据行 -->
              <div class="match-row home-row">
                <div class="team-info">
                  <img :src="match.home_team?.logo" alt="Home Team" class="team-logo" />
                  <span class="team-name">{{ match.home_team?.name }}</span>
                </div>
            <div class="row-data">
              <span class="col-quarter" :class="getSectionScoreClass(match.home_section_score[0], match.guest_section_score[0], match.match_status)">{{ statusMatchScoreText(match.home_section_score[0], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.home_section_score[1], match.guest_section_score[1], match.match_status)">{{ statusMatchScoreText(match.home_section_score[1], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.home_section_score[2], match.guest_section_score[2], match.match_status)">{{ statusMatchScoreText(match.home_section_score[2], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.home_section_score[3], match.guest_section_score[3], match.match_status)">{{ statusMatchScoreText(match.home_section_score[3], match.match_status) }}</span>
              <span class="col-total total-score">{{ statusMatchScoreText(match.home_total_score, match.match_status) }}</span>
              <span class="col-half">{{ calculateHalfScore(match.home_section_score, match.match_status, 'first') }}/{{ calculateHalfScore(match.home_section_score, match.match_status, 'second') }}</span>
              <span class="col-diff">{{ calculateDiff(match, match.match_status, 'home') }}</span>
              <span class="col-score">{{ calculateTotal(match, match.match_status, 'home') }}</span>
              <span class="col-live">
                <img
                  src="@assets/icons/score/animation-active.webp"
                  v-show="hasAnimationIcon(match)"
                  class="live-icon"
                />
                <img 
                  src="@assets/icons/score/video-active.webp" 
                  v-show="hasVideoIcon(match)"
                  class="live-icon"
                />
              </span>
              <span class="col-odds">{{ formatOdds(match.w_1?.[0]) }}</span>
              <span class="col-odds">{{ formatOdds(match.w_1?.[1]) }}</span>
              <span class="col-odds">{{ formatOdds(match.w_1?.[2]) }}</span>
              <span class="col-actions action-row">
                <p class="action-link">分析</p>
                <img src="@assets/icons/score/stick.webp" alt="Stick" class="action-icon" />
              </span>
              </div>
              </div>

              <!-- 客队数据行 -->
              <div class="match-row away-row">
                <div class="team-info">
                  <img :src="match.guest_team?.logo" alt="Away Team" class="team-logo" />
                  <span class="team-name">{{ match.guest_team?.name }}</span>
                </div>
            <div class="row-data">
              <span class="col-quarter" :class="getSectionScoreClass(match.guest_section_score[0], match.home_section_score[0], match.match_status)">{{ statusMatchScoreText(match.guest_section_score[0], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.guest_section_score[1], match.home_section_score[1], match.match_status)">{{ statusMatchScoreText(match.guest_section_score[1], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.guest_section_score[2], match.home_section_score[2], match.match_status)">{{ statusMatchScoreText(match.guest_section_score[2], match.match_status) }}</span>
              <span class="col-quarter" :class="getSectionScoreClass(match.guest_section_score[3], match.home_section_score[3], match.match_status)">{{ statusMatchScoreText(match.guest_section_score[3], match.match_status) }}</span>
              <span class="col-total total-score">{{ statusMatchScoreText(match.guest_total_score, match.match_status) }}</span>
              <span class="col-half">{{ calculateHalfScore(match.guest_section_score, match.match_status, 'first') }}/{{ calculateHalfScore(match.guest_section_score, match.match_status, 'second') }}</span>
              <span class="col-diff">{{ calculateDiff(match, match.match_status, 'away') }}</span>
              <span class="col-score">{{ calculateTotal(match, match.match_status, 'away') }}</span>
              <span class="col-live"></span>
              <span class="col-odds">{{ formatOdds(match.w_2?.[0]) }}</span>
              <span class="col-odds">{{ formatOdds(match.w_2?.[1]) }}</span>
              <span class="col-odds">{{ formatOdds(match.w_2?.[2]) }}</span>
              <span class="col-actions action-row">
                <p class="action-link">指数</p>
                <img src="@assets/icons/score/collect.webp" alt="Collect" class="action-icon" />
              </span>
              </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  list: {
    type: Array,
    default: () => []
  },
  hasReset: {
    type: Boolean,
    default: false
  }
})

const matches = ref([])

// 监听列表变化
watch(
  () => props.list,
  (newList) => {
    matches.value = newList || []
  },
  { immediate: true }
)

// 按状态分组
const groupedMatches = computed(() => {
  const groups = {
    inProgress: [],
    notStarted: [],
    finished: []
  }

  matches.value.forEach((match) => {
    if (match.match_status === 2) {
      groups.inProgress.push(match)
    } else if (match.match_status === 1) {
      groups.notStarted.push(match)
    } else if (match.match_status === 3) {
      groups.finished.push(match)
    }
  })

  return groups
})

// 获取分组标题
const getGroupTitle = (status) => {
  switch (status) {
    case 'inProgress':
      return '进行中'
    case 'notStarted':
      return '未开始'
    case 'finished':
      return '已结束'
    default:
      return ''
  }
}

// 格式化时间
const formatTime = (timestamp) => {
  return dayjs(timestamp).format('HH:mm')
}

// 根据状态显示时间
const timeShowHandle = (statusIndex) => {
  return statusIndex === 1 || statusIndex === 3
}

// 根据状态显示分数文本
const statusMatchScoreText = (score, statusIndex) => {
  if (statusIndex === 1) {
    return '-'
  }
  return score ?? '-'
}

// 计算半场得分
const calculateHalfScore = (sectionScores, status, half) => {
  if (status === 1) return '-'
  
  if (half === 'first') {
    const score = (sectionScores[0] || 0) + (sectionScores[1] || 0)
    return score || '-'
  } else {
    const score = (sectionScores[2] || 0) + (sectionScores[3] || 0)
    return score || '-'
  }
}

// 计算分差
const calculateDiff = (match, status, team) => {
  if (status === 1) return '-'
  
  const homeTotalScore = match.home_total_score || 0
  const guestTotalScore = match.guest_total_score || 0
  
  if (team === 'home') {
    const diff = homeTotalScore - guestTotalScore
    if (diff > 0) return `+${diff}`
    if (diff === 0) return '0'
    return diff
  } else {
    const diff = guestTotalScore - homeTotalScore
    if (diff > 0) return `+${diff}`
    if (diff === 0) return '0'
    return diff
  }
}

// 计算总分
const calculateTotal = (match, status, team) => {
  if (status === 1) return '-'
  
  const homeTotalScore = match.home_total_score || 0
  const guestTotalScore = match.guest_total_score || 0
  const total = homeTotalScore + guestTotalScore
  
  return total || '-'
}

// 格式化赔率
const formatOdds = (odds) => {
  if (!odds && odds !== 0) return '-'
  return odds
}

// 判断是否有动画图标
const hasAnimationIcon = (match) => {
  return match.has_vid !== 1 && match.has_anim === 1
}

// 判断是否有视频图标
const hasVideoIcon = (match) => {
  return match.has_vid === 1
}

// 判断单节得分的高低，返回对应的CSS类
const getSectionScoreClass = (currentScore, opponentScore, status) => {
  // 如果比赛未开始，返回空
  if (status === 1) {
    return ''
  }
  
  const current = currentScore || 0
  const opponent = opponentScore || 0
  
  // 如果得分低于对手，返回灰色类
  if (current < opponent) {
    return 'score-lower'
  }
  
  // 得分高于或等于对手，保持默认黑色
  return ''
}
</script>

<style lang="scss" scoped>
.match-list-standard {
  width: 100%;
}

.group-header {
  padding: 10px 15px;
  background-color: transparent;
  color: $scoreBasketball-matchList-text-title;
  margin-bottom: 10px;
  text-align: center;
  font-size: 12px;
}

.match-item {
  background: $scoreBasketball-matchList-white;
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
  position: relative;
  
  &.match-in-progress::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #FF4444;
    border-radius: 12px 0 0 12px;
  }
  
  &.match-not-started::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #4A90E2;
    border-radius: 12px 0 0 12px;
  }
}

.match-header {
  display: flex;
  align-items: center;
  height: 35px;
  border-bottom: 1px solid $scoreBasketball-matchList-border-divider;
  
  .header-left {
    display: flex;
    align-items: center;
    width: 320px;
    padding-left: 20px;
    
    .league-logo {
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
    
    .league-name {
      font-weight: 500;
      font-size: 12px;
      color: $scoreBasketball-matchList-text-dark;
    }
  }
  
  .header-columns {
    display: flex;
    align-items: center;
    flex: 1;
    font-weight: 400;
    font-size: 12px;
    color: $scoreBasketball-matchList-text-dark;
    
    span {
      text-align: center;
      padding: 0 5px;
    }
    
    .col-quarter {
      width: 45px;
    }
    
    .col-total {
      width: 55px;
      margin-left: 30px;
    }
    
    .col-half {
      width: 70px;
      margin-left: 20px;
    }
    
    .col-diff {
      width: 50px;
    }
    
    .col-score {
      width: 50px;
    }
    
    .col-live {
      width: 45px;
      margin-left: 20px;
      
      + .col-odds-title {
        margin-left: 20px;
      }
    }
    
    .col-odds-title {
      width: 65px;
    }
    
    .col-actions {
      width: 110px;
    }
  }
}

.match-data-wrapper {
  display: flex;
  
  .match-left-fixed {
    display: flex;
    align-items: center;
    padding-left: 10px;
    
    .select-icon {
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        width: 20px;
        height: 20px;
        cursor: pointer;
      }
    }
    
    .status-info {
      width: 70px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: $scoreBasketball-matchList-text-gray;
      gap: 1px;
      
      &.in-progress {
        color: $scoreBasketball-matchList-text-red;
        
        .status-text {
          font-weight: bold;
        }
      }
      
      .status-text {
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
        text-align: center;
      }
      
      .time-text {
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
        text-align: center;
      }
    }
  }
  
  .match-right-data {
    flex: 1;
  }
}

.match-row {
  display: flex;
  align-items: center;
  min-height: 28px;
  padding: 0;
  
  &.home-row {
    padding-top: 2px;
  }
  
  &.away-row {
    padding-bottom: 2px;
  }
  
  .team-info {
    display: flex;
    align-items: center;
    width: 200px;
    padding-left: 30px;
      
    .team-logo {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }
    
    .team-name {
      font-size: 12px;
      color: $scoreBasketball-matchList-text-dark;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 150px;
    }
  }
  
  .row-data {
    display: flex;
    align-items: center;
    flex: 1;
    font-size: 12px;
    color: $scoreBasketball-matchList-text-dark;
    
    span {
      text-align: center;
      padding: 0 5px;
      
      &.score-lower {
        color: #999999;
      }
    }
    
    .col-quarter {
      width: 45px;
    }
    
    .col-total {
      width: 55px;
      margin-left: 30px;
      font-size: 15px;
      font-weight: bold;
      
      &.total-score {
        color: $scoreBasketball-matchList-text-red;
      }
    }
    
    .col-half {
      width: 70px;
      margin-left: 20px;
      color: #999999;
    }
    
    .col-diff {
      width: 50px;
    }
    
    .col-score {
      width: 50px;
    }
    
    .col-live {
      width: 45px;
      margin-left: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      
      .live-icon {
        width: 20px;
        height: 20px;
      }
      
      + .col-odds {
        margin-left: 20px;
      }
    }
    
    .col-odds {
      width: 65px;
    }
    
    .col-actions {
      width: 110px;
      
      &.action-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 18px;
        
        .action-link {
          font-size: 12px;
          color: $scoreBasketball-matchList-text-light-gray;
          cursor: pointer;
          margin: 0;
          line-height: 1;
          white-space: nowrap;
          
          &:hover {
            color: $scoreBasketball-matchList-text-darker-gray;
          }
        }
        
        .action-icon {
          width: 12px;
          height: 12px;
          cursor: pointer;
        }
      }
    }
  }
}
</style>

